import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  Search, 
  Filter, 
  Package, 
  ShoppingCart,
  ChevronDown,
  TrendingUp,
  BarChart3,
  Eye,
  Star,
  Activity,
  DollarSign,
  Tag,
  Building,
  List,
  LayoutGrid
} from 'lucide-react';
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { LoadingScreen } from '@/components/common';
import { formatCurrency } from '@/lib/utils';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

interface SubscriptionPlanFamily {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
}

interface SubscriptionPlanPrice {
  id: string;
  subscription_plan_id: string;
  currency: string;
  price_amount: number;
  cost_amount?: number;
  margin?: number;
}

interface SubscriptionPlan {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  family_id: string;
  sku?: string;
  pricing_model?: string;
  unit_measure?: string;
  billing_frequency: string;
  prices?: SubscriptionPlanPrice[];
  family?: SubscriptionPlanFamily;
  created_at?: string;
  updated_at?: string;
}

interface CatalogSummary {
  totalProducts: number;
  activeProducts: number;
  familyCount: number;
  avgPrice: number;
}

export default function ProductCatalog() {
  const navigate = useNavigate();
  const [selectedFamily, setSelectedFamily] = useState('all');
  const [planFamilies, setPlanFamilies] = useState<SubscriptionPlanFamily[]>([]);
  const [products, setProducts] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [summaryData, setSummaryData] = useState<CatalogSummary>({
    totalProducts: 0,
    activeProducts: 0,
    familyCount: 0,
    avgPrice: 0
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'table' | 'cards'>('cards');
  const { toast } = useToast();

  const fetchPlanFamilies = async () => {
    try {
      const { data, error } = await supabase
        .from('subscription_plan_families')
        .select('*')
        .eq('is_active', true)
        .order('name', { ascending: true });

      if (error) throw error;
      setPlanFamilies(data || []);
      
      setSummaryData(prev => ({
        ...prev,
        familyCount: data?.length || 0
      }));
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Erro ao carregar famílias de produtos',
        description: 'Não foi possível carregar as famílias de produtos. Por favor, recarregue a página.',
      });
    }
  };

  const fetchProducts = async () => {
    try {
      let query = supabase
        .from('subscription_plans')
        .select(`
          *,
          prices:subscription_plan_prices (*),
          family:subscription_plan_families (*)
        `)
        .order('name', { ascending: true });

      // Apply family filter
      if (selectedFamily !== 'all') {
        query = query.eq('family_id', selectedFamily);
      }

      // Apply active filter
      if (!showInactive) {
        query = query.eq('is_active', true);
      }

      // Apply search filter
      if (searchTerm) {
        query = query.or(`name.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%,sku.ilike.%${searchTerm}%`);
      }

      const { data, error } = await query;

      if (error) throw error;
      setProducts(data || []);

      // Calculate summary data
      const activeProducts = (data || []).filter(p => p.is_active).length;
      const totalProducts = (data || []).length;
      
      // Calculate average price from first price of each product
      const prices = (data || [])
        .filter(p => p.prices && p.prices.length > 0)
        .map(p => p.prices![0].price_amount);
      const avgPrice = prices.length > 0 ? prices.reduce((a, b) => a + b, 0) / prices.length : 0;

      setSummaryData(prev => ({
        ...prev,
        totalProducts,
        activeProducts,
        avgPrice
      }));
    } catch (error) {
      console.error('Error fetching products:', error);
      toast({
        variant: 'destructive',
        title: 'Erro ao carregar produtos',
        description: 'Não foi possível carregar os produtos. Por favor, tente novamente.',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlanFamilies();
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [selectedFamily, showInactive, searchTerm]);

  const formatBillingFrequency = (frequency: string) => {
    switch (frequency) {
      case 'month':
        return 'Mensal';
      case 'quarter':
        return 'Trimestral';
      case 'semiannual':
        return 'Semestral';
      case 'year':
        return 'Anual';
      case 'one-time':
        return 'Pagamento Único';
      default:
        return frequency;
    }
  };

  const formatPricingModel = (model?: string, unitMeasure?: string) => {
    if (!model) return '';
    
    switch (model) {
      case 'flat':
        return 'Valor Fixo';
      case 'per unit':
        return `Por Unidade${unitMeasure ? ` (${unitMeasure})` : ''}`;
      default:
        return model;
    }
  };

  const formatBillingPeriod = (type?: string, duration?: number) => {
    if (!type || !duration) return '-';
    
    const typeLabel = type === 'month' ? 'Mês(es)' : type === 'year' ? 'Ano(s)' : type;
    return `${duration} ${typeLabel}`;
  };

  if (loading) {
    return <LoadingScreen message="Carregando Catálogo..." size="x-small" />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      {/* Modern Header with Gradient Background */}
      <div className="relative overflow-hidden" style={{ backgroundColor: '#1F1746' }}>
        {/* SVG Background Element */}
        <div className="absolute inset-0 pointer-events-none">
          <svg 
            className="opacity-30"
            width="1690"
            height="1026.5433"
            viewBox="0 0 826.54 502.06" 
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="xMidYMid slice"
          >
            <g transform="scale(1.1) translate(-70, -315)">
              <path d="M0 0h826.54v502.06H0z" fill="none"/>
              <path d="M0 0h826.54v502.06H0z" fill="url(#a)"/>
              <defs>
                <linearGradient id="a" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#6366f1" stopOpacity=".1"/>
                  <stop offset="100%" stopColor="#8b5cf6" stopOpacity=".1"/>
                </linearGradient>
              </defs>
            </g>
          </svg>
        </div>
        <div className="absolute inset-0 bg-black/20" />
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-white/5" />
        
        <div className="relative container mx-auto px-6 py-6 pb-8">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                  <ShoppingCart className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-extrabold text-white">Catálogo de Produtos</h1>
                  <p className="text-blue-100 text-base pb-2">
                    Explore nossos produtos e planos disponíveis
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button 
                variant="secondary" 
                size="default" 
                onClick={() => navigate('/subscription-plans')} 
                className="backdrop-blur-sm bg-white/20 border-white/30 text-white hover:bg-white/30 transition-all duration-200"
              >
                <Package className="h-4 w-4 mr-2" />
                Gerenciar Planos
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 -mt-4 relative z-10">
        {/* Enhanced Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-blue-50 to-white">
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-blue-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-blue-100 rounded-md">
                    <Package className="h-4 w-4 text-blue-600" />
                  </div>
                  <span className="text-sm font-extrabold text-gray-600">Total de Produtos</span>
                </div>
                <div className="text-2xl font-extrabold text-gray-900">
                  {summaryData.totalProducts}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="text-xs text-gray-500">
                  Em {summaryData.familyCount} famílias
                </div>
                <div className="flex items-center gap-1">
                  <TrendingUp className="h-3 w-3 text-blue-500" />
                  <span className="text-xs text-green-600 font-extrabold">+12%</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-green-50 to-white">
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-green-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-green-100 rounded-md">
                    <Activity className="h-4 w-4 text-green-600" />
                  </div>
                  <span className="text-sm font-extrabold text-gray-600">Produtos Ativos</span>
                </div>
                <div className="text-2xl font-extrabold text-gray-900">
                  {summaryData.activeProducts}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="text-xs text-gray-500">
                  {summaryData.totalProducts > 0 
                    ? `${Math.round((summaryData.activeProducts / summaryData.totalProducts) * 100)}% do total` 
                    : 'Nenhum produto'}
                </div>
                <div className="flex items-center gap-1">
                  <Activity className="h-3 w-3 text-green-500" />
                  <span className="text-xs text-green-600 font-extrabold">+5%</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-purple-50 to-white">
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-purple-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-purple-100 rounded-md">
                    <Building className="h-4 w-4 text-purple-600" />
                  </div>
                  <span className="text-sm font-extrabold text-gray-600">Famílias</span>
                </div>
                <div className="text-2xl font-extrabold text-gray-900">
                  {summaryData.familyCount}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="text-xs text-gray-500">
                  Categorias disponíveis
                </div>
                <div className="flex items-center gap-1">
                  <Star className="h-3 w-3 text-purple-500" />
                  <span className="text-xs text-purple-600 font-extrabold">+3</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-orange-50 to-white">
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-orange-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-orange-100 rounded-md">
                    <DollarSign className="h-4 w-4 text-orange-600" />
                  </div>
                  <span className="text-sm font-extrabold text-gray-600">Preço Médio</span>
                </div>
                <div className="text-2xl font-extrabold text-gray-900">
                  {formatCurrency(summaryData.avgPrice, 'BRL')}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="text-xs text-gray-500">
                  Por produto
                </div>
                <div className="flex items-center gap-1">
                  <BarChart3 className="h-3 w-3 text-orange-500" />
                  <span className="text-xs text-orange-600 font-extrabold">+8%</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Enhanced Main Content */}
        <Card className="border shadow-sm bg-white/80 backdrop-blur-sm">
          <CardContent className="p-6">
            {/* Enhanced Filter Section */}
            <div className="flex items-center justify-between gap-4 mb-6">
              <div className="flex items-center gap-4 flex-1">
                <div className="min-w-0 flex-1">
                  <Select value={selectedFamily} onValueChange={setSelectedFamily}>
                    <SelectTrigger className="h-10 bg-white border-gray-200 focus:border-blue-500 focus:ring-blue-500">
                      <SelectValue placeholder="Família de Produtos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all" className="py-3">
                        <div>
                          <div className="font-extrabold">Todas as Famílias</div>
                          <div className="text-xs text-gray-500 truncate max-w-48">
                            Mostrar produtos de todas as famílias
                          </div>
                        </div>
                      </SelectItem>
                      {planFamilies.map((family) => (
                        <SelectItem key={family.id} value={family.id} className="py-3">
                          <div>
                            <div className="font-extrabold">{family.name}</div>
                            {family.description && (
                              <div className="text-xs text-gray-500 truncate max-w-48">
                                {family.description}
                              </div>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="relative min-w-0 flex-1 max-w-sm">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 text-gray-400 transform -translate-y-1/2" />
                  <Input
                    placeholder="Pesquisar produtos..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 h-10 bg-white border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>

                <Popover open={isFilterOpen} onOpenChange={setIsFilterOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="h-10 px-4 border-gray-200 hover:bg-gray-50">
                      <Filter className="h-4 w-4 mr-2" />
                      Filtros
                      {(showInactive) && (
                        <Badge variant="secondary" className="ml-2 h-4 w-4 p-0 text-xs">
                          1
                        </Badge>
                      )}
                      <ChevronDown className="h-3 w-3 ml-2 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 p-6" align="end">
                    <div className="space-y-6">
                      <div>
                        <h4 className="font-extrabold text-gray-900 mb-1">Filtros Avançados</h4>
                        <p className="text-sm text-gray-500">Personalize a visualização dos produtos</p>
                      </div>
                      
                      <Separator />
                      
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div className="space-y-1">
                            <label className="text-sm font-extrabold text-gray-700">
                              Mostrar produtos inativos
                            </label>
                            <p className="text-xs text-gray-500">
                              Incluir produtos desativados na listagem
                            </p>
                          </div>
                          <Checkbox 
                            checked={showInactive} 
                            onCheckedChange={(checked) => setShowInactive(!!checked)} 
                          />
                        </div>
                      </div>
                      
                      <div className="flex justify-between pt-4 border-t">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => {
                            setSearchTerm('');
                            setShowInactive(false);
                            setSelectedFamily('all');
                          }}
                        >
                          Limpar Filtros
                        </Button>
                        <Button size="sm" onClick={() => setIsFilterOpen(false)}>
                          Aplicar
                        </Button>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              {/* View Mode Toggle */}
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('cards')}
                  className={`flex items-center gap-2 px-3 py-2 text-sm font-extrabold rounded-md transition-colors ${
                    viewMode === 'cards'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <LayoutGrid className="h-4 w-4" />
                  Cards
                </button>
                <button
                  onClick={() => setViewMode('table')}
                  className={`flex items-center gap-2 px-3 py-2 text-sm font-extrabold rounded-md transition-colors ${
                    viewMode === 'table'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <List className="h-4 w-4" />
                  Tabela
                </button>
              </div>
            </div>

            {/* Products Display */}
            {products.length > 0 ? (
              <>
                {/* Cards View */}
                {viewMode === 'cards' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {products.map((product) => (
                      <Card key={product.id} className="group hover:shadow-lg transition-all duration-200 cursor-pointer border-gray-200 hover:border-blue-300">
                        <CardContent className="p-6" onClick={() => navigate(`/product-catalog/${product.id}`)}>
                          <div className="space-y-4">
                            {/* Product Header */}
                            <div className="space-y-2">
                              <div className="flex items-start justify-between">
                                <h3 className="font-extrabold text-gray-900 text-lg leading-tight group-hover:text-blue-600 transition-colors">
                                  {product.name}
                                </h3>
                                <Badge 
                                  variant={product.is_active ? "default" : "secondary"}
                                  className={`text-xs ${product.is_active ? "bg-green-500 hover:bg-green-600" : "bg-gray-500"}`}
                                >
                                  {product.is_active ? "Ativo" : "Inativo"}
                                </Badge>
                              </div>
                              
                              {product.sku && (
                                <div className="flex items-center gap-2">
                                  <Tag className="h-3 w-3 text-gray-400" />
                                  <span className="text-xs text-gray-500 font-mono">{product.sku}</span>
                                </div>
                              )}
                              
                              {product.family?.name && (
                                <div className="flex items-center gap-2">
                                  <Building className="h-3 w-3 text-gray-400" />
                                  <span className="text-xs text-gray-500">{product.family.name}</span>
                                </div>
                              )}
                            </div>

                            {/* Product Description */}
                            {product.description && (
                              <p className="text-sm text-gray-600 line-clamp-2">
                                {product.description}
                              </p>
                            )}

                            {/* Product Details */}
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <span className="text-xs text-gray-500">Cobrança:</span>
                                <span className="text-xs font-extrabold text-gray-700">
                                  {formatBillingFrequency(product.billing_frequency)}
                                </span>
                              </div>

                              {product.pricing_model && (
                                <div className="flex items-center justify-between">
                                  <span className="text-xs text-gray-500">Modelo:</span>
                                  <span className="text-xs font-extrabold text-gray-700">
                                    {formatPricingModel(product.pricing_model, product.unit_measure)}
                                  </span>
                                </div>
                              )}
                            </div>

                            {/* Price Section */}
                            <div className="pt-3 border-t border-gray-100">
                              {product.prices && product.prices.length > 0 ? (
                                <div className="space-y-2">
                                  <div className="flex items-center justify-between">
                                    <span className="text-sm text-gray-500">Preço:</span>
                                    <div className="text-right">
                                      <div className="text-lg font-extrabold text-green-600">
                                        {formatCurrency(product.prices[0].price_amount, product.prices[0].currency)}
                                      </div>
                                      {product.prices[0].currency && (
                                        <div className="text-xs text-gray-500">
                                          {product.prices[0].currency}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  
                                  {product.prices.length > 1 && (
                                    <div className="text-xs text-blue-600">
                                      +{product.prices.length - 1} moeda{product.prices.length > 2 ? 's' : ''}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="text-center py-2">
                                  <span className="text-sm text-gray-500">Preço não definido</span>
                                </div>
                              )}
                            </div>

                            {/* Action Button */}
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="w-full group-hover:bg-blue-50 group-hover:border-blue-300 transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/product-catalog/${product.id}`);
                              }}
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              Ver Detalhes
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}

                {/* Table View */}
                {viewMode === 'table' && (
                  <div className="border rounded-lg overflow-hidden bg-white">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead className="font-extrabold">Produto</TableHead>
                          <TableHead className="font-extrabold">Família</TableHead>
                          <TableHead className="font-extrabold">Preço</TableHead>
                          <TableHead className="font-extrabold">Cobrança</TableHead>
                          <TableHead className="font-extrabold">Período de Assinatura</TableHead>
                          <TableHead className="font-extrabold">Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {products.map((product) => (
                          <TableRow 
                            key={product.id} 
                            className="hover:bg-gray-50 cursor-pointer transition-colors"
                            onClick={() => navigate(`/product-catalog/${product.id}`)}
                          >
                            <TableCell className="font-extrabold">
                              <div className="max-w-[250px]">
                                <div className="font-extrabold text-gray-900 truncate">
                                  {product.name}
                                </div>
                                {product.sku && (
                                  <div className="text-xs text-gray-500 font-mono mt-1">
                                    {product.sku}
                                  </div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm text-gray-600">
                                {product.family?.name || '-'}
                              </span>
                            </TableCell>
                            <TableCell>
                              {product.prices && product.prices.length > 0 ? (
                                <div>
                                  <div className="font-extrabold text-green-600">
                                    {formatCurrency(product.prices[0].price_amount, product.prices[0].currency)}
                                  </div>
                                  {product.prices.length > 1 && (
                                    <div className="text-xs text-blue-600">
                                      +{product.prices.length - 1} moeda{product.prices.length > 2 ? 's' : ''}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <span className="text-sm text-gray-500">Não definido</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <span className="text-sm text-gray-600">
                                {formatBillingFrequency(product.billing_frequency)}
                              </span>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm text-gray-600">
                                {formatBillingPeriod(product.subscription_period_type, product.subscription_period_duration)}
                              </span>
                            </TableCell>
                            <TableCell>
                              <Badge 
                                variant={product.is_active ? "default" : "secondary"}
                                className={`text-xs ${product.is_active ? "bg-green-500 hover:bg-green-600" : "bg-gray-500"}`}
                              >
                                {product.is_active ? "Ativo" : "Inativo"}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-20">
                <div className="space-y-6">
                  <div className="w-24 h-24 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center mx-auto">
                    <ShoppingCart className="h-12 w-12 text-blue-500" />
                  </div>
                  <div className="space-y-3">
                    <h3 className="text-xl font-extrabold text-gray-900">
                      Nenhum produto encontrado
                    </h3>
                    <p className="text-gray-500 max-w-lg mx-auto leading-relaxed">
                      Não encontramos produtos que correspondam aos seus critérios de pesquisa. 
                      Tente ajustar os filtros ou termos de busca.
                    </p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button 
                      onClick={() => {
                        setSearchTerm('');
                        setShowInactive(false);
                        setSelectedFamily('all');
                      }}
                      variant="outline"
                      size="lg"
                    >
                      Limpar Filtros
                    </Button>
                    <Button onClick={() => navigate('/subscription-plans')} size="lg">
                      <Package className="h-5 w-5 mr-2" />
                      Gerenciar Planos
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Bottom Spacing */}
      <div className="h-16" />
    </div>
  );
}
